import React from 'react';
import { Box, Card, CardContent, CircularProgress, Stack, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useGetPMSummaryQuery, useGetPMByCenterQuery, useGetPMByCategoryQuery } from '../../../services/ReportingService';
import { useGetCentersQuery, useGetPMCategoriesQuery } from '../../../services/DimensionService/dimensionApi';
import {
  PMReportHeader,
  PMReportFilters,
  PMReportSummaryCards,
  PMReportCharts,
} from '../components';
import { usePMReportFilters } from '../hooks';

const PMReportPage: React.FC = () => {
  const {
    centerId, setCenterId,
    category, setCategory,
    fromDate, setFromDate,
    toDate, setToDate,
    appliedFilters,
    handleApplyFilters,
    handleResetFilters,
  } = usePMReportFilters();

  // Dimension data
  const { data: centers = [], isLoading: centersLoading } = useGetCentersQuery();
  const { data: pmCategories = [], isLoading: categoriesLoading } = useGetPMCategoriesQuery();

  // Report data
  const { data: summaryData, isLoading: summaryLoading } = useGetPMSummaryQuery(appliedFilters);
  const { data: centerData = [], isLoading: centerLoading } = useGetPMByCenterQuery(appliedFilters);
  const { data: categoryData = [], isLoading: categoryLoading } = useGetPMByCategoryQuery(appliedFilters);

  const isLoading = summaryLoading || centerLoading || categoryLoading || centersLoading || categoriesLoading;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc', py: 1.5, px: 2 }}>
        <Box display="flex" justifyContent="center">
          <Card sx={{
            minWidth: 1200,
            width: '100%',
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            border: '1px solid #e2e8f0',
            overflow: 'hidden',
          }}>
            <PMReportHeader />

            <Card sx={{ m: 2, border: '1px solid #e2e8f0' }} elevation={0}>
              <PMReportFilters
                centers={centers}
                categories={pmCategories}
                centerId={centerId}
                category={category}
                fromDate={fromDate}
                toDate={toDate}
                onCenterChange={setCenterId}
                onCategoryChange={setCategory}
                onFromDateChange={setFromDate}
                onToDateChange={setToDate}
                onApply={handleApplyFilters}
                onReset={handleResetFilters}
              />
            </Card>

            <CardContent sx={{ p: 3, backgroundColor: '#ffffff' }}>
              {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                  <CircularProgress />
                </Box>
              ) : summaryData ? (
                <Stack spacing={3}>
                  <PMReportSummaryCards summaryData={summaryData} />
                  <PMReportCharts
                    summaryData={summaryData}
                    centerData={centerData}
                    categoryData={categoryData}
                  />
                </Stack>
              ) : (
                <Box sx={{
                  minWidth: 1200,
                  display: 'flex',
                  justifyContent: 'center',
                  py: 8,
                }}>
                  <Typography variant="body1" color="#64748b">
                    No PM data available. Adjust your filters and try again.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default PMReportPage;
